import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import Swal from "sweetalert2";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/system";
import Api from "../../utils/Api";
import { useListing } from "../../contexts/ListingContext";

import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState, Modifier } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from 'html-to-draftjs';
import DOMPurify from 'dompurify';

import { Select } from 'antd';
const { Option } = Select;

const ListingFormContainer = styled(Box)(({ theme }) => ({
  color: "#fff",
  maxWidth: 1020,
  margin: "20px auto",
  padding: "40px",
  borderRadius: "20px",
  // background: "linear-gradient(135deg, #1E1E1E, #2A2A2A)",
  backgroundColor: 'rgb(28, 28, 28, 0.95)',
  // boxShadow:
  //   "0px 19px 38px rgba(11, 211, 73, 0.25), 0px 15px 12px rgba(11, 211, 73, 0.15)",
}));

const ListingField = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginBottom: "30px",
}));

const Label = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: "#0BD349",
  marginBottom: "8px",
  fontSize: "18px",
  letterSpacing: "0.5px",
}));

const FileInput = styled("input")({
  display: "none",
});

const ImagePreview = styled("img")({
  marginTop: "10px",
  maxWidth: "100%",
  borderRadius: "12px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  border: "2px solid #0BD349",
});

const StyledCheckbox = styled(Checkbox)({
  color: "#0BD349",
  "&.Mui-checked": {
    color: "#0BD349",
  },
  "&:hover": {
    backgroundColor: "rgba(11, 211, 73, 0.1)",
  },
});

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#0BD349",
  color: "#000",
  boxShadow: 'none',
  fontWeight: "bold",
  padding: "12px 24px",
  borderRadius: "8px",
  textTransform: "uppercase",
  marginLeft: "10px",
  "&:hover": {
    backgroundColor: "#09A145",
  },
  "&:disabled": {
    backgroundColor: "#666",
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#fff",
  color: "#000",
  fontWeight: "bold",
  padding: "12px 24px",
  borderRadius: "8px",
  textTransform: "uppercase",
  "&:hover": {
    backgroundColor: "#c1c1c1",
  },
}));

function AddListing({ edit = false }) {
  const user = useSelector((state) => state.user.user);
  const { fetchListings } = useListing();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user.userData.active) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Your status is inactive. Please contact us to activate your account.",
        iconColor: 'red',
        confirmButtonColor: '#000'
      });
      navigate("/dashboard/listings");
    }
  }, [user, navigate])

  const [categories, setCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [listingData, setListingData] = useState({
    listingPhoto: null,
    title: "",
    hallNo: user.userData?.hall_no ?? "",
    standNo: user.userData?.booth_location ?? "",
    description: "",
    companyWebsite: "",
    uploaded_by: user.loggedinUserId,
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams(); // Get the listing ID from URL params
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await Api.get("/listing-categories");
        if (response.data.success) {
          setCategories(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    // Fetch categories when component mounts
    fetchCategories();

    // Fetch listing data if in edit mode
    const fetchListingData = async () => {
      if (edit && id) {
        try {
          const response = await Api.get(`/listingsById/${id}`);
          if (response.data.success) {
            const data = response.data.data;
            console.log(data);

            const blocksFromHtml = htmlToDraft(data.listing_description);
            const { contentBlocks, entityMap } = blocksFromHtml;
            const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
            const editorState = EditorState.createWithContent(contentState);

            setEditorState(editorState);

            setListingData({
              ...listingData,
              title: data.listing_title,
              hallNo: data?.hallNo || '',
              standNo: data?.standNo || '',
              description: data.listing_description,
              companyWebsite: data.company_website,
              listingPhoto: data.listing_image,
              uploaded_by: data.uploaded_by,
            });
            setPhotoPreview(data.listing_image);
            setSelectedCategoryIds(data?.listing_category.split(","));
          }
        } catch (error) {
          console.error("Error fetching listing data:", error);
          alert("Error fetching listing data");
        }
      }
    };

    fetchListingData();
  }, [edit, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setListingData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectCategories = (value) => {
    setSelectedCategoryIds(value);
    // const { value, checked } = e.target;
  };

  // const handleCheckboxChange = (e) => {
  //   const { value, checked } = e.target;
  //   setSelectedCategoryIds((prev) =>
  //     checked ? [...prev, value] : prev.filter((id) => id !== value)
  //   );
  // };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 11 * 1024 * 1024; // 10MB in bytes

      if (file.size > maxSize) {
        alert("File size should not exceed 10MB!");
        e.target.value = ""; // Reset input field
        return;
      }
      try {
        const compressedBase64String = await compressAndConvertToBase64(file);
        setListingData((prev) => ({
          ...prev,
          listingPhoto: compressedBase64String,
        }));
        setPhotoPreview(compressedBase64String);
      } catch (error) {
        console.error("Error processing image:", error);
        alert("Error processing image. Please try again.");
      }
    } else {
      setListingData((prev) => ({
        ...prev,
        listingPhoto: null,
      }));
      setPhotoPreview(null);
    }
  };

  const compressAndConvertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target.result;
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set desired dimensions, e.g., 1000x1000 pixels
        const maxWidth = 1000;
        const maxHeight = 1000;
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        // Resize canvas and draw the image
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to JPEG and get Base64 string
        const base64String = canvas.toDataURL('image/jpeg', 0.7); // Adjust quality as needed (0.7 is 70%)

        resolve(base64String);
      };

      img.onerror = (error) => {
        reject(error);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file); // Start reading the file
    });
  };


  const handleCancel = () => {
    navigate("/dashboard/listings"); // Navigate to listings page
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!listingData.title.trim()) {
        // throw new Error("Title is required");
        throw new Error("Titel ist erforderlich");
      }
      if (!listingData.hallNo.trim()) {
        // throw new Error("Hall no. is required");
        throw new Error("Halle Nr. ist erforderlich");
      }
      if (!listingData.description.trim()) {
        // throw new Error("Description is required");
        throw new Error("Beschreibung ist erforderlich");
      }
      if (!listingData.companyWebsite.trim()) {
        // throw new Error("Company website is required");
        throw new Error("Unternehmenswebsite ist erforderlich");
      }
      if (selectedCategoryIds.length === 0) {
        // throw new Error("Please select at least one category");
        throw new Error("Bitte wählen Sie mindestens eine Kategorie aus");
      }

      if (!listingData?.listingPhoto) {
        // throw new Error("Please upload a photo");
        throw new Error("Bitte laden Sie ein Foto hoch");
      }

      const payload = {
        title: listingData.title.trim(),
        hallNo: listingData.hallNo.trim(),
        standNo: listingData.standNo.trim(),
        description: listingData.description.trim(),
        company_website: listingData.companyWebsite.trim(),
        uploaded_by: listingData.uploaded_by,
        category_ids: selectedCategoryIds.join(","),
        listingPhoto: listingData?.listingPhoto,
      };

      let response;
      if (edit) {
        // Update the existing listing
        response = await Api.put(`/edit-listing/${id}`, payload);
      } else {
        // Create a new listing
        response = await Api.post("/add-listings", payload);
      }

      if (response.data.success) {
        fetchListings();
        setListingData({
          listingPhoto: null,
          title: "",
          hallNo: user.userData?.hall_no || "",
          standNo: user.userData?.booth_location || "",
          description: "",
          companyWebsite: "",
          uploaded_by: user?.loggedinUserId,
        });
        setSelectedCategoryIds([]);
        setPhotoPreview(null);
        Swal.fire({
          icon: "success",
          title: edit ? "Eintrag aktualisiert" : "Eintrag hinzugefügt",
          text: response.data.message,
          iconColor: '#000',
          confirmButtonColor: '#000'
        });
        navigate("/dashboard/listings");
      } else {
        throw new Error(response.data.message || "Failed to process listing");
      }
    } catch (error) {
      // console.error("Error submitting listing:", error);
      Swal.fire({
        icon: "error",
        title: error?.response?.status === 429 ? "Limit erreicht" : error?.response?.data?.message || error?.message || "Error processing listing",
        text: error?.response?.data?.message || error?.message || "Error processing listing",
        iconColor: '#e10000',
        confirmButtonColor: '#e10000'
      });
      // alert(error.message || "Error processing listing");
    } finally {
      setIsSubmitting(false);
    }
  };

  // const onEditorStateChange = (es) => {
  //   setEditorState(es);
  //   setListingData(prev => ({
  //     ...prev,
  //     description: draftToHtml(convertToRaw(es.getCurrentContent()))
  //   }));
  // }

  const CHARACTER_LIMIT = 2500;

  const toolbarOptions = {
    options: ['inline', 'blockType', 'history'],
    inline: {
      options: ['bold', 'italic', 'underline'],
    },
    blockType: {
      options: ['Normal', 'H4'],
    },
  };

  const editorLabels = {
    'components.controls.blocktype.h4': 'Überschrift',
    'components.controls.blocktype.normal': 'Normal',
  };

  const truncateContent = (content) => {
    return content.slice(0, CHARACTER_LIMIT);
  };

  const onEditorStateChange = (es) => {
    const plainText = es.getCurrentContent().getPlainText();

    if (plainText.length > CHARACTER_LIMIT) {
      const truncatedText = truncateContent(plainText);
      const newContentState = ContentState.createFromText(truncatedText);
      const newEditorState = EditorState.push(es, newContentState, 'change-block-data');

      setEditorState(newEditorState);
      setListingData((prev) => ({
        ...prev,
        description: DOMPurify.sanitize(
          draftToHtml(convertToRaw(newContentState))
        ),
      }));
    } else {
      setEditorState(es);
      setListingData((prev) => ({
        ...prev,
        description: DOMPurify.sanitize(
          draftToHtml(convertToRaw(es.getCurrentContent()))
        ),
      }));
    }
  };

  const handlePastedText = (text, html, editorState, onChange) => {
    try {
      const currentContent = editorState.getCurrentContent();
      const selection = editorState.getSelection();

      // Ensure the new text is inserted at the current cursor position
      const contentStateWithInsertedText = Modifier.insertText(
        currentContent,
        selection,
        text
      );

      // Enforce character limit on the entire text
      const plainText = contentStateWithInsertedText.getPlainText();
      const truncatedText = truncateContent(plainText);

      // Create new content state with the truncated text
      const newContent = ContentState.createFromText(truncatedText);

      // Update the editor state with the new content
      const newState = EditorState.push(editorState, newContent, 'insert-characters');
      onChange(newState);

      return true; // Prevent default paste behavior 
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div
      style={{
        position: 'relative'
        // minHeight: "84vh",
        // background: "linear-gradient(to left, #0BD349, #000000)",
      }}
    >
      <img style={{
        width: '100%',
        objectFit: 'contain',
        position: 'fixed',
        zIndex: -1
      }} src="https://cdn.prod.website-files.com/65f03c978efea1c906592c94/65f41d5f2ba41fbbe6c5514c_AdobeStock_546815906_gruen_klein_bearbeitet_002.webp" />
      <ListingFormContainer>
        <Typography variant="h4" gutterBottom style={{ color: "#0BD349" }}>
          {edit ? "Eintrag bearbeiten" : "Erstellen Sie einen neuen Eintrag"}
        </Typography>

        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <ListingField>
            <Label>Titel des Eintrags:</Label>
            <TextField
              name="title"
              value={listingData.title}
              onChange={handleInputChange}
              variant="outlined"
              borderColor="none"
              fullWidth
              sx={{ color: "#fff", backgroundColor: "#333" }}
              InputProps={{ style: { color: "#fff" } }}
            />
          </ListingField>

          <ListingField>
            <Label>Halle:</Label>
            <TextField
              inputProps={{
                maxLength: 3
              }}
              name="hallNo"
              value={listingData.hallNo}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              sx={{ color: "#fff", backgroundColor: "#333" }}
              InputProps={{ style: { color: "#fff" } }}
            />
          </ListingField>

          <ListingField>
            <Label>Standplatz:</Label>
            <TextField
              inputProps={{
                maxLength: 4
              }}
              name="standNo"
              value={listingData.standNo}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              sx={{ color: "#fff", backgroundColor: "#333" }}
              InputProps={{ style: { color: "#fff" } }}
            />
          </ListingField>

          <ListingField>
            <Label>Beschreibung des Eintrags:</Label>
            <div style={{ position: 'relative' }}>
              <Editor
                placeholder="Beschreibung..."
                toolbar={toolbarOptions}
                localization={{ locale: 'en', translations: editorLabels }}
                editorState={editorState}
                toolbarClassName="toolbar"
                wrapperClassName="wrapper"
                editorClassName="editor"
                onEditorStateChange={onEditorStateChange}
                handlePastedText={(text, html) => handlePastedText(text, html, editorState, setEditorState)}
              />
              <span
                style={{
                  position: 'absolute',
                  right: 5,
                  bottom: 1,
                  fontSize: 8,
                  color: 'gray',
                }}
              >
                Max. 2500 Zeichen
              </span>
            </div>
            {/* <TextField
            name="description"
            value={listingData.description}
            onChange={handleInputChange}
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            sx={{ color: "#fff", backgroundColor: "#333" }}
            InputProps={{ style: { color: "#fff" } }}
          /> */}
          </ListingField>

          <ListingField>
            <Label>Unternehmenswebsite:</Label>
            <TextField
              name="companyWebsite"
              value={listingData.companyWebsite}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              sx={{ color: "#fff", backgroundColor: "#333" }}
              InputProps={{ style: { color: "#fff" } }}
            />
          </ListingField>

          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <ListingField sx={{ flex: "1 1 50%" }}>
              <Label>Kategorie wählen:</Label>
              <Select
                className="custom-select"
                mode="multiple"
                showSearch
                style={{ width: '100%' }}
                placeholder="Select categories"
                dropdownStyle={{
                  backgroundColor: '#333', // Dark background for dropdown options
                  color: '#fff'            // White text color for dropdown options
                }}
                value={selectedCategoryIds}
                onChange={handleSelectCategories}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {categories.map((category) => (
                  <Option style={{ backgroundColor: '#333', color: '#fff' }} key={category.id} value={category.id.toString()}>
                    {category.category_name}
                  </Option>
                ))}
              </Select>
              {/* {categories.map((category) => (
              <FormControlLabel
                key={category.id}
                control={
                  <StyledCheckbox
                    value={category.id}
                    checked={selectedCategoryIds.includes(category.id.toString())}
                    onChange={handleCheckboxChange}
                  />
                }
                label={category.category_name}
              />
            ))} */}
            </ListingField>

            <ListingField sx={{ flex: "1 1 50%" }}>
              <Label>Auflistungsfoto:</Label>
              <FileInput
                type="file"
                onChange={handleFileChange}
                accept="image/*"
                id="listing-photo-upload"
              />
              <label htmlFor="listing-photo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  style={{
                    backgroundColor: "#333",
                    color: "#fff",
                    borderColor: "#0BD349",
                  }}
                >
                  Foto hochladen
                </Button>
              </label>
              {photoPreview && <ImagePreview src={photoPreview} alt="Preview" />}
            </ListingField>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 3 }}>
            <CancelButton onClick={handleCancel}>
              {/* Cancel */}
              Abbrechen
            </CancelButton>
            <StyledButton
              type="submit"
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Einreichen..." : edit ? "Eintrag aktualisieren" : "Eintrag einreichen"}
            </StyledButton>
          </Box>
        </form>
      </ListingFormContainer>
    </div>
  );
}

export default AddListing;